import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ContactProfile } from "@/components/contacts/ContactProfile";
import { CreateContactModal } from "@/components/contacts/CreateContactModal";
import { Contact } from "@/types/crm";
import {
  Plus,
  Search,
  Mail,
  Phone,
  Building2,
  Edit,
  Loader2,
  Users,
  Trash2,
  Target,
  Calendar,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useContacts } from "@/hooks/useContacts";
import { contactoAPI } from "@/services/contactoService";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { LoadingState } from "@/components/ui/loading-state";
import { DateRange } from "react-day-picker";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { opportunityAPI } from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";

export default function Contacts() {
  const auth = useAuth();
  const {
    contacts: apiContacts,
    statuses: customerStatuses = [],
    error,
    refreshContacts,
    isLoading: contactsLoading,
  } = useContacts();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [saving, setSaving] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<DateRange | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  const { company } = useAuth();
  const [allOpportunities, setAllOpportunities] = useState<any[]>([]);

  useEffect(() => {
    if (Array.isArray(apiContacts)) {
      setContacts(apiContacts);
    }
  }, [apiContacts]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  useEffect(() => {
    const fetchOpportunities = async () => {
      if (!company?.slug) return;
      try {
        const response = await opportunityAPI.getOpportunities({
          slug: company.slug,
          limit: 1000,
        });
        setAllOpportunities(response.data || []);
      } catch (error) {
        console.error("Error fetching opportunities:", error);
      }
    };
    fetchOpportunities();
  }, [company?.slug]);

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      `${contact.firstName} ${contact.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.position.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company?.name.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesDate = true;
    const createdAt = (contact as any).createdAt || (contact as any).created_at;
    if (dateFilter?.from && createdAt) {
      const date = new Date(createdAt);
      date.setHours(0, 0, 0, 0);
      
      const from = new Date(dateFilter.from);
      from.setHours(0, 0, 0, 0);
      
      if (dateFilter.to) {
        const to = new Date(dateFilter.to);
        to.setHours(23, 59, 59, 999);
        matchesDate = date >= from && date <= to;
      } else {
        matchesDate = date.getTime() === from.getTime();
      }
    }

    return matchesSearch && matchesDate;
  });

  const ITEMS_PER_PAGE = 9;
  const currentTotalPages = Math.ceil(filteredContacts.length / ITEMS_PER_PAGE) || 1;
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter]);

  const hasFilters = searchQuery.length > 0 || dateFilter?.from;

  const clearFilters = () => {
    setSearchQuery("");
    setDateFilter(undefined);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= currentTotalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const isDuplicateContact = (contact: any) => {
    if (!contact.email) return false;
    return contacts.some(
      (existingContact) =>
        existingContact.email &&
        existingContact.email.toLowerCase() === contact.email.toLowerCase() &&
        existingContact.id !== contact.id,
    );
  };

  const handleSaveContact = async (contact: any) => {
    if (!company?.slug) {
      toast({
        title: "Error",
        description: "No se pudo identificar la empresa",
        variant: "destructive",
      });
      return;
    }

    if (isDuplicateContact(contact)) {
      toast({
        title: "Contacto duplicado",
        description: "Ya existe un contacto con este email",
        variant: "destructive",
      });
      throw new Error("Contacto duplicado");
    }

    try {
      setSaving(true);
      if (editingContact) {
        const updateData = {
          name:
            contact.name || `${contact.firstName} ${contact.lastName}`.trim(),
          position: contact.position || "",
          phone: contact.phone,
          email: contact.email,
          client_company_id: contact.client_company_id || contact.companyId,
          contact_status_id: contact.contact_status_id || contact.statusId,
          notes: contact.notes || "",
          communication_preferences: contact.communication_preferences || [],
          source_id: contact.sourceId || "",
        };
        const response = await contactoAPI.updateContacto(
          company!.slug,
          editingContact.id,
          updateData,
        );
        console.log("Respuesta de actualización:", response);

        toast({
          title: "Contacto actualizado",
          description: "El contacto ha sido actualizado exitosamente",
        });
      } else {
        const createData = {
          slug: company.slug,
          name:
            contact.name || `${contact.firstName} ${contact.lastName}`.trim(),
          position: contact.position || "",
          phone: contact.phone,
          email: contact.email,
          client_company_id: contact.client_company_id || contact.companyId,
          contact_status_id: contact.contact_status_id || contact.statusId,
          notes: contact.notes || "",
          communication_preferences: contact.communication_preferences || [],
          source_id: contact.sourceId || "",
        };
        const response = await contactoAPI.createContacto(
          company.slug,
          createData,
        );
        toast({
          title: "Contacto creado",
          description: "El contacto ha sido creado exitosamente",
        });
      }
      setEditingContact(null);
      setIsCreateModalOpen(false);
      await refreshContacts();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.body?.error ||
        error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "No se pudo guardar el contacto";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (contact: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingContact(contact);
    setIsCreateModalOpen(true);
  };

  const handleDeleteContact = (contactId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setContactToDelete(contactId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;
    try {
      await contactoAPI.deleteContacto(company!.slug, contactToDelete);
      toast({
        title: "Contacto eliminado",
        description: "El contacto ha sido eliminado exitosamente",
      });
      await refreshContacts();
    } catch (error: any) {
      console.error("Error al eliminar contacto:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "No se pudo eliminar el contacto",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    }
  };

  const totalContacts = contacts.length;
  const contactsWithOpportunities = contacts.filter((c) => {
    return allOpportunities.some((opp) => {
      const oppContactId =
        typeof opp.contact === "object" ? opp.contact?.id : opp.contact;
      return oppContactId === c.id || opp.contact_id === c.id;
    });
  }).length;
  const contactsWithCompany = contacts.filter(
    (c) => c.company || c.companyId,
  ).length;

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const currentMonthName = new Date().toLocaleString("es-ES", {
    month: "long",
  });
  const newContactsThisMonth = contacts.filter((c) => {
    if (!c.createdAt) return false;
    const date = new Date(c.createdAt);
    return (
      date.getMonth() === currentMonth && date.getFullYear() === currentYear
    );
  }).length;


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-indigo-100 p-2.5 rounded-2xl">
              <Users className="h-7 w-7 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Contactos</h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">Gestiona todos tus contactos</p>
        </div>

        {auth.can("contacts.create") && (
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
            <Button
              className="w-full sm:w-auto justify-center bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all"
              onClick={() => {
                setEditingContact(null);
                setIsCreateModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Contacto
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-slate-50/80 border border-slate-200/80 rounded-[20px] p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1">Total Contactos</p>
            <p className="text-2xl font-black text-slate-900 tracking-tight">
              {totalContacts}
            </p>
          </div>
          <div className="h-10 w-10 bg-blue-100/50 text-blue-600 shadow-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ring-1 ring-blue-100">
            <Users className="h-5 w-5" />
          </div>
        </div>

        <div className="bg-slate-50/80 border border-slate-200/80 rounded-[20px] p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1">Con Oportunidades</p>
            <p className="text-2xl font-black text-emerald-500 tracking-tight">
              {contactsWithOpportunities}
            </p>
          </div>
          <div className="h-10 w-10 bg-emerald-100/50 text-emerald-600 shadow-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ring-1 ring-emerald-100">
            <Target className="h-5 w-5" />
          </div>
        </div>

        <div className="bg-slate-50/80 border border-slate-200/80 rounded-[20px] p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1">
              Nuevos este Mes{" "}
              <span className="capitalize text-slate-400 font-medium">
                ({currentMonthName})
              </span>
            </p>
            <p className="text-2xl font-black text-orange-500 tracking-tight">
              {newContactsThisMonth}
            </p>
          </div>
          <div className="h-10 w-10 bg-orange-100/50 text-orange-500 shadow-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ring-1 ring-orange-100">
            <UserPlus className="h-5 w-5" />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar contactos por nombre, email o cargo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
          <div className="w-full sm:w-auto min-w-[260px]">
            <DatePickerWithRange date={dateFilter} setDate={setDateFilter} />
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full px-4"
            >
              <X className="h-4 w-4 mr-2" />
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {contactsLoading ? (
        <div className="mt-8">
          <LoadingState type="grid" message="Cargando contactos..." />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedContacts.map((contact) => {
              const opportunityCount = allOpportunities.filter((opp) => {
                const oppContactId =
                  typeof opp.contact === "object" ? opp.contact?.id : opp.contact;
                return oppContactId === contact.id || opp.contact_id === contact.id;
              }).length;

          return (
            <Card
              key={contact.id}
              className="cursor-pointer bg-indigo-50/40 rounded-[24px] border-2 border-indigo-100/50 shadow-md hover:shadow-xl hover:border-indigo-300/60 hover:bg-white transition-all duration-300 hover:-translate-y-1 overflow-hidden group"
              onClick={() => handleContactClick(contact)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-slate-900/30 ring-1 ring-white/10 shrink-0">
                      {contact.firstName?.charAt(0)}
                      {contact.lastName?.charAt(0)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-semibold text-slate-900 truncate"
                        title={`${contact.firstName} ${contact.lastName}`}
                      >
                        {contact.firstName} {contact.lastName}
                      </h3>
                      <p
                        className={`text-sm truncate ${contact.position ? "text-slate-600" : "text-slate-400 italic"}`}
                      >
                        {contact.position || "Sin cargo"}
                      </p>

                      <div className="flex items-center space-x-1 mt-2">
                        <Building2
                          className={`h-3 w-3 flex-shrink-0 ${contact.company?.name ? "text-indigo-500" : "text-slate-300"}`}
                        />
                        <span
                          className={`text-xs truncate ${contact.company?.name ? "text-slate-500" : "text-slate-400 italic"}`}
                        >
                          {contact.company?.name || "Sin empresa"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    {auth.can("contacts.update") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleEditClick(contact, e)}
                        className="h-8 w-8 p-0 text-slate-900 hover:text-black hover:bg-slate-100"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {auth.can("contacts.delete") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDeleteContact(contact.id, e)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2.5">
                  <div className="flex items-center text-sm">
                    <Mail
                      className={`h-4 w-4 mr-2 flex-shrink-0 ${contact.email ? "text-blue-500" : "text-slate-300"}`}
                    />
                    {contact.email ? (
                      <span
                        className="truncate text-slate-600"
                        title={contact.email}
                      >
                        {contact.email}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic text-xs">
                        Email no proporcionado
                      </span>
                    )}
                  </div>

                  <div className="flex items-center text-sm">
                    <Phone
                      className={`h-4 w-4 mr-2 flex-shrink-0 ${contact.phone ? "text-green-500" : "text-slate-300"}`}
                    />
                    {contact.phone ? (
                      <span className="text-slate-600">
                        {contact.countryCode ? `${contact.countryCode} ` : ""}
                        {contact.phone}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic text-xs">
                        Teléfono no proporcionado
                      </span>
                    )}
                  </div>

                  <div className="flex items-center text-sm">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0 text-purple-500" />
                    <span className="text-xs text-slate-500">
                      Registrado:{" "}
                      {contact.createdAt
                        ? new Date(contact.createdAt).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <Badge
                    variant="secondary"
                    className={
                      opportunityCount > 0
                        ? "bg-slate-800 text-white font-medium"
                        : "bg-slate-100 text-slate-600 font-normal"
                    }
                  >
                    Oportunidades: {opportunityCount}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>



          {filteredContacts.length > 0 && (
            <div className="flex flex-col items-center gap-6 mt-8">
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: currentTotalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`h-10 w-10 rounded-full text-sm font-medium transition-all ${
                        currentPage === i + 1 
                          ? "bg-slate-900 text-white shadow-md shadow-slate-900/30" 
                          : "text-slate-600 hover:bg-slate-100 hover:text-indigo-600"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === currentTotalPages}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="text-sm font-medium text-slate-500 bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
                Mostrando <span className="text-slate-900">{paginatedContacts.length}</span> de <span className="text-slate-900">{filteredContacts.length}</span> contactos
              </div>
            </div>
          )}

          {filteredContacts.length === 0 && (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No se encontraron contactos
              </h3>
              <p className="text-slate-600 mb-4">
                {searchQuery
                  ? "Intenta ajustar tu búsqueda"
                  : "Comienza agregando tu primer contacto"}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => {
                    setEditingContact(null);
                    setIsCreateModalOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Contacto
                </Button>
              )}
            </div>
          )}
        </>
      )}

      <ContactProfile
        contact={selectedContact}
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
      />

      <CreateContactModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        existingContact={editingContact}
        customerStatuses={customerStatuses}
        existingContacts={contacts}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              el contacto y todos sus datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div >
  );
}
